'use client'

import { useState, useEffect, useCallback, useRef } from 'react'
import type {
  CalculateQuoteInput,
  QuoteCalculation,
  QuoteTierSelection,
} from '@/lib/types/operations/quote'

interface UseQuoteCalculatorInput extends QuoteTierSelection {
  shopLocationId: string | null
  make?: string
  model?: string
  year?: number
  trim?: string
  serviceIds: string[]
  enabled?: boolean
}

interface UseQuoteCalculatorResult {
  calculation: QuoteCalculation | null
  isCalculating: boolean
  error: string | null
  recalculate: () => void
}

export function useQuoteCalculator(input: UseQuoteCalculatorInput): UseQuoteCalculatorResult {
  const [calculation, setCalculation] = useState<QuoteCalculation | null>(null)
  const [isCalculating, setIsCalculating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)

  const {
    shopLocationId,
    make,
    model,
    year,
    trim,
    serviceIds,
    materialTier,
    coverageTier,
    decalPackage,
    ppfCoverage,
    filmTier,
    colouredPpfCoverage,
    enabled = true,
  } = input

  const calculate = useCallback(async () => {
    if (!enabled || !shopLocationId || serviceIds.length === 0) {
      setCalculation(null)
      return
    }

    abortControllerRef.current?.abort()
    const controller = new AbortController()
    abortControllerRef.current = controller

    setIsCalculating(true)
    setError(null)

    try {
      const body: CalculateQuoteInput = {
        shopLocationId,
        serviceIds,
        make,
        model,
        year,
        trim,
        materialTier,
        coverageTier,
        decalPackage,
        ppfCoverage,
        filmTier,
        colouredPpfCoverage,
      }

      const response = await fetch('/api/quotes/calculate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      })

      if (!response.ok) {
        throw new Error('Failed to calculate quote')
      }

      const result: QuoteCalculation = await response.json()
      setCalculation(result)
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setIsCalculating(false)
    }
  }, [
    shopLocationId,
    make,
    model,
    year,
    trim,
    serviceIds,
    materialTier,
    coverageTier,
    decalPackage,
    ppfCoverage,
    filmTier,
    colouredPpfCoverage,
    enabled,
  ])

  useEffect(() => {
    const timer = setTimeout(calculate, 300)
    return () => clearTimeout(timer)
  }, [calculate])

  return { calculation, isCalculating, error, recalculate: calculate }
}
